import type {
  IHeatmapFilterPayload,
  EHeatmapFilterType,
  EHeatmapDraftBaseFilterType,
  IHeatmapBaseFilterPayload,
  IHeatmapDraftBaseFilterPayload,
} from '../../types';
import type { IHeatmapFilterStore } from '../../stores';

import { createViewContextHook } from '@gem-sdk/heatmap-react';
import { useHeatmapFilterStore } from '../../stores';
import { EHeatmapBaseFilterType } from '../../types';
import { isDeepEqual } from '~/utils';

interface IHeatmapFilterState {
  baseFilters: IHeatmapBaseFilterPayload | undefined;
  draftBaseFilters: IHeatmapDraftBaseFilterPayload | undefined;
  activeFilters: IHeatmapFilterPayload | undefined;
  draftFilters: IHeatmapFilterPayload | undefined;
  hasFilterChanges: boolean;
  getBaseFilter: <K extends EHeatmapBaseFilterType>(
    filterType: K,
  ) => IHeatmapBaseFilterPayload[K] | undefined;
}

interface IHeatmapFilterActions {
  setBaseFilter: <K extends EHeatmapBaseFilterType>(
    filterType: K,
    value: IHeatmapBaseFilterPayload[K],
  ) => void;
  setDraftBaseFilter: <K extends EHeatmapDraftBaseFilterType>(
    filterType: K,
    value: IHeatmapDraftBaseFilterPayload[K],
  ) => void;
  setDraftBaseFilters: (payload: Partial<IHeatmapDraftBaseFilterPayload>) => void;
  clearDraftBaseFilters: () => void;
  /** Set filter for active and draft filters by filter type */
  setFilter: <K extends EHeatmapFilterType>(filterType: K, value: IHeatmapFilterPayload[K]) => void;
  /** Set draft filter for draft filters by filter type */
  setDraftFilter: <K extends EHeatmapFilterType>(
    filterType: K,
    value: IHeatmapFilterPayload[K],
  ) => void;
  /** Set draft filters for draft filters by payload */
  setDraftFilters: (payload: Partial<IHeatmapFilterPayload>) => void;
  removeFilter: (filterType: EHeatmapFilterType) => void;
  applyFilters: () => void;
  applyBaseFilters: () => void;
  cancelFilters: () => void;
  resetFilters: () => void;
  initializeFilters: (payload: Partial<IHeatmapFilterPayload>) => void;
  initializeBaseFilters: (payload: Partial<IHeatmapBaseFilterPayload>) => void;
  clearView: (viewId: string) => void;
}

export const useHeatmapFilterContext = createViewContextHook<
  IHeatmapFilterStore,
  IHeatmapFilterState,
  IHeatmapFilterActions
>({
  useStore: useHeatmapFilterStore,

  getState: (store, viewId) => {
    const baseFilters = store.base.get(viewId);
    const draftBaseFilters = store.draftBase.get(viewId);
    const activeFilters = store.data.get(viewId);
    const draftFilters = store.draftData.get(viewId);

    return {
      baseFilters,
      draftBaseFilters,
      activeFilters,
      draftFilters,
      hasFilterChanges: checkFilterChanges({
        activeFilters,
        draftFilters,
        baseFilters,
        draftBaseFilters,
      }),
      getBaseFilter: (filterType) => baseFilters?.[filterType],
    };
  },

  getActions: (store, viewId) => ({
    setBaseFilter: (filterType, value) => store.setBaseFilter(filterType, value, viewId),
    setDraftBaseFilter: (filterType, value) => store.setDraftBaseFilter(filterType, value, viewId),
    setDraftBaseFilters: (payload) => store.setDraftBaseFilters(payload, viewId),
    clearDraftBaseFilters: () => store.clearDraftBaseFilters(viewId),
    setFilter: (filterType, value) => store.setFilter(filterType, value, viewId),
    setDraftFilter: (filterType, value) => store.setDraftFilter(filterType, value, viewId),
    setDraftFilters: (payload) => store.setDraftFilters(payload, viewId),
    removeFilter: (filterType) => store.removeFilter(filterType, viewId),
    applyFilters: () => store.applyFilters(viewId),
    applyBaseFilters: () => store.applyBaseFilters(viewId),
    cancelFilters: () => store.cancelFilters(viewId),
    resetFilters: () => store.resetFilters(viewId),
    initializeFilters: (payload) => store.initializeFilters(payload, viewId),
    initializeBaseFilters: (payload) => store.initializeBaseFilters(payload, viewId),
    clearView: (viewId) => store.clearView(viewId),
  }),
});

// ===== Helpers =====
const hasDraftBaseChanges = (
  base: IHeatmapBaseFilterPayload | undefined,
  draftBase: IHeatmapDraftBaseFilterPayload | undefined,
) => {
  if (draftBase == null || Object.keys(draftBase).length === 0) return false;

  const relevantBase: IHeatmapDraftBaseFilterPayload = {};
  if (base?.[EHeatmapBaseFilterType.ANALYTIC_MODE]) {
    relevantBase[EHeatmapBaseFilterType.ANALYTIC_MODE] = base[EHeatmapBaseFilterType.ANALYTIC_MODE];
  }
  if (base?.[EHeatmapBaseFilterType.OPERATOR]) {
    relevantBase[EHeatmapBaseFilterType.OPERATOR] = base[EHeatmapBaseFilterType.OPERATOR];
  }

  return !isDeepEqual(relevantBase, draftBase);
};

const checkFilterChanges = (params: {
  activeFilters: IHeatmapFilterPayload | undefined;
  draftFilters: IHeatmapFilterPayload | undefined;
  baseFilters: IHeatmapBaseFilterPayload | undefined;
  draftBaseFilters: IHeatmapDraftBaseFilterPayload | undefined;
}) => {
  if (!isDeepEqual(params.activeFilters, params.draftFilters)) return true;

  return hasDraftBaseChanges(params.baseFilters, params.draftBaseFilters);
};
